/**
 * A agenda da clínica (ADR-0028): cada parte da tela da recepção (buscar
 * paciente, vagas, agendar…) → a operação SQL que a atende nesta organização.
 *
 * Os nomes são os da recepção, e não os da finalidade no código. O `Record`
 * por `FinalidadeDaAgenda` obriga a parte nova a ganhar nome aqui.
 */

import {
  ESCRITAS_DA_AGENDA,
  ErroDaApi,
  PARAMETROS_DA_AGENDA,
  type FinalidadeDaAgenda,
  type OperacaoDaApi,
  type CampoComPadrao,
  type ConferenciasDaAgenda,
  type PadroesDaAgenda,
} from '@cliqcrm/contracts';

export interface ParteDaAgenda {
  readonly parte: FinalidadeDaAgenda;
  readonly nome: string;
  /** O que a parte faz, dito para quem liga a operação. */
  readonly ajuda: string;
  readonly grava: boolean;
  readonly parametros: readonly string[];
}

const DITO: Readonly<Record<FinalidadeDaAgenda, { nome: string; ajuda: string }>> = {
  buscar_paciente: { nome: 'Buscar paciente pelo CPF', ajuda: 'Devolve os pacientes daquele CPF.' },
  buscar_paciente_por_telefone: {
    nome: 'Buscar paciente pelo telefone',
    ajuda: 'O telefone da conversa, com DDD e sem o 55.',
  },
  buscar_paciente_por_termo: {
    nome: 'Buscar paciente pelo nome ou código',
    ajuda: 'Nome, sobrenome ou código, como a recepção digita.',
  },
  ler_paciente: { nome: 'Ler o paciente', ajuda: 'O cadastro de um paciente pelo código.' },
  historico_paciente: {
    nome: 'Consultas do paciente',
    ajuda: 'As consultas que o card do paciente mostra (as 5 mais recentes).',
  },
  ultimas_vezes_paciente: {
    nome: 'Últimas vezes do paciente',
    ajuda: 'Os atendimentos, com movimento, procedimentos, convênio e clínica.',
  },
  exames_paciente: {
    nome: 'Exames do paciente',
    ajuda: 'Os exames que o paciente fez, para o prontuário: data, exame e médico.',
  },
  detalhes_dos_atendimentos: {
    nome: 'Detalhes dos atendimentos',
    ajuda:
      'O que cada card do funil é: serviço, médico, quando agendou, o dia marcado e o valor, pelas referências das rotinas.',
  },
  vagas: {
    nome: 'Horários disponíveis do médico',
    ajuda: 'Os horários disponíveis do médico entre duas datas.',
  },
  dias_com_vaga: {
    nome: 'Dias com horário disponível',
    ajuda:
      'Quantos horários disponíveis o médico tem em cada dia de um mês. Ligada, o agendamento anda pelos meses; sem ela, mostra só os próximos 14 dias.',
  },
  bloqueios: { nome: 'Bloqueios da agenda', ajuda: 'Os períodos em que o médico não atende.' },
  medicos: { nome: 'Médicos', ajuda: 'Todos os médicos, com código e nome.' },
  medicos_por_especialidade: {
    nome: 'Médicos de uma especialidade',
    ajuda: 'Os médicos que atendem a especialidade escolhida.',
  },
  especialidades: { nome: 'Especialidades', ajuda: 'Todas as especialidades, com código e nome.' },
  convenios: { nome: 'Convênios', ajuda: 'Os convênios que a clínica atende, com código e nome.' },
  planos: { nome: 'Planos do convênio', ajuda: 'Os planos ativos do convênio escolhido.' },
  origens: {
    nome: 'Origens do agendamento',
    ajuda: 'Por onde o pedido chegou: atendimento, telefone, WhatsApp, site.',
  },
  locais: { nome: 'Locais de atendimento', ajuda: 'As unidades onde a consulta acontece.' },
  cadastrar_paciente: { nome: 'Cadastrar paciente', ajuda: 'Grava um paciente novo na clínica.' },
  agendar: { nome: 'Agendar', ajuda: 'Ocupa o horário escolhido com o paciente.' },
  remarcar: {
    nome: 'Atualizar agendamento',
    ajuda:
      'O "Editar" da consulta marcada: grava os dados novos e, se o horário mudou, passa o paciente para o horário novo e devolve o antigo, numa escrita só.',
  },
  ler_agendamento: {
    nome: 'Ler o agendamento',
    ajuda: 'O que está gravado no horário agendado, para o "Editar" começar com os dados dele.',
  },
  liberar_vaga: {
    nome: 'Cancelar agendamento',
    ajuda: 'Devolve o horário, com o motivo e, se pedido, o paciente na fila de espera.',
  },
  registrar_cancelamento: {
    nome: 'Registrar o cancelamento',
    ajuda:
      'Grava o cancelamento com o motivo, como a "Exclusão de horário" do SISAC (no SISAC, na LogSistema). Roda antes de liberar o horário: sem o registro, nada é cancelado.',
  },
  motivos_de_cancelamento: {
    nome: 'Motivos de cancelamento',
    ajuda: 'Os motivos que a clínica usa ao cancelar, com código e nome.',
  },
  precos_de_consulta: {
    nome: 'Preços das consultas',
    ajuda:
      'O preço particular da consulta de cada médico (código do médico, do procedimento, nome e preço), e o padrão da clínica sem médico.',
  },
  precos_de_procedimentos: {
    nome: 'Preços dos exames e procedimentos',
    ajuda: 'O preço particular de cada exame e procedimento (código, nome, grupo, tipo e preço).',
  },
};

/** As partes na ordem da tela: o paciente, a agenda, e as que gravam no fim. */
export const PARTES_DA_AGENDA: readonly ParteDaAgenda[] = (
  Object.keys(PARAMETROS_DA_AGENDA) as FinalidadeDaAgenda[]
)
  .map((parte) => ({
    parte,
    ...DITO[parte],
    grava: ESCRITAS_DA_AGENDA.includes(parte),
    parametros: PARAMETROS_DA_AGENDA[parte],
  }))
  .sort((a, b) => Number(a.grava) - Number(b.grava));

/**
 * As operações que podem atender a parte: do mesmo tipo (leitura ou escrita).
 * As não publicadas ficam de fora, menos a que já está ligada: ela aparece
 * para a pessoa ver o que está lá, e o servidor recusa ao salvar.
 */
export function operacoesDaParte(
  parte: ParteDaAgenda,
  operacoes: readonly OperacaoDaApi[],
  ligada: string | undefined,
): OperacaoDaApi[] {
  return operacoes
    .filter((op) => op.tipo === (parte.grava ? 'escrita' : 'leitura'))
    .filter((op) => op.situacao === 'publicada' || op.id === ligada)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** O mapa a gravar: só as partes com operação escolhida. */
export function mapaParaGravar(rascunho: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(Object.entries(rascunho).filter(([, id]) => id !== ''));
}

/** O rascunho mudou em relação ao que está gravado? */
export function mudou(
  rascunho: Readonly<Record<string, string>>,
  gravado: Readonly<Record<string, string>>,
): boolean {
  const a = mapaParaGravar(rascunho);
  const chaves = new Set([...Object.keys(a), ...Object.keys(gravado)]);
  return [...chaves].some((chave) => a[chave] !== gravado[chave]);
}

/**
 * Os problemas que o servidor apontou, por parte (`operacoes.<parte>`), e os
 * que valem para o mapa inteiro (conexões diferentes).
 */
export function problemasDoMapa(erro: unknown): {
  porParte: Record<string, string>;
  /** Os da conferência de cada escrita (#1187), por parte. */
  porConferencia: Record<string, string>;
  geral: string[];
} {
  const porParte: Record<string, string> = {};
  const porConferencia: Record<string, string> = {};
  const geral: string[] = [];
  if (!(erro instanceof ErroDaApi)) return { porParte, porConferencia, geral };
  for (const falha of erro.problema.errors ?? []) {
    if (falha.campo.startsWith('operacoes.')) porParte[falha.campo.slice(10)] = falha.problema;
    else if (falha.campo.startsWith('conferencias.')) {
      porConferencia[falha.campo.slice(13)] = falha.problema;
    } else geral.push(falha.problema);
  }
  return { porParte, porConferencia, geral };
}

// ── A escrita conferida (#1187) ─────────────────────────────────────────

/** As conferências a gravar: só as que têm leitura e pelo menos um campo. */
export function conferenciasParaGravar(c: Readonly<ConferenciasDaAgenda>): ConferenciasDaAgenda {
  return Object.fromEntries(
    Object.entries(c).filter(([, v]) => v && v.operacao !== '' && v.campos.length > 0),
  ) as ConferenciasDaAgenda;
}

/** As conferências mudaram em relação às gravadas? */
export function conferenciasMudaram(
  rascunho: Readonly<ConferenciasDaAgenda>,
  gravado: Readonly<ConferenciasDaAgenda>,
): boolean {
  const texto = (c: Readonly<ConferenciasDaAgenda>) =>
    JSON.stringify(
      Object.entries(conferenciasParaGravar(c))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([parte, v]) => [parte, v!.operacao, [...v!.campos].sort()]),
    );
  return texto(rascunho) !== texto(gravado);
}

/** Os padrões do agendamento (#1187), na ordem do formulário da recepção. */
export const PADROES_DA_AGENDA: readonly {
  readonly chave: CampoComPadrao;
  readonly rotulo: string;
  readonly exemplo: string;
}[] = [
  { chave: 'local', rotulo: 'Local', exemplo: 'Ex.: 0100' },
  { chave: 'origem', rotulo: 'Origem', exemplo: 'Ex.: Atendimento' },
  { chave: 'convenio', rotulo: 'Convênio', exemplo: 'Ex.: 000' },
  { chave: 'plano', rotulo: 'Plano', exemplo: 'Ex.: UNICO' },
];

/** A lista da clínica de onde sai cada padrão. */
export const LISTA_DO_PADRAO: Readonly<
  Record<CampoComPadrao, 'locais' | 'origens' | 'convenios' | 'planos'>
> = { local: 'locais', origem: 'origens', convenio: 'convenios', plano: 'planos' };

/**
 * As opções do dropdown: as da lista e, se o padrão gravado não estiver nela
 * (a clínica tirou o código), ele também, marcado, para não sumir sem aviso.
 */
export function opcoesDoPadrao(
  itens: readonly { readonly codigo: string; readonly nome: string }[],
  valor: string,
): { valor: string; rotulo: string }[] {
  const opcoes = itens.map((item) => ({
    valor: item.codigo,
    rotulo: item.nome === item.codigo ? item.nome : `${item.nome} (${item.codigo})`,
  }));
  return valor !== '' && !itens.some((item) => item.codigo === valor)
    ? [{ valor, rotulo: `${valor} (não está na lista da clínica)` }, ...opcoes]
    : opcoes;
}

/**
 * Os padrões a gravar: aparados, só os preenchidos, e os ocultos só entre eles
 * (escondido sem valor, o campo sumiria vazio).
 */
export function padroesParaGravar(padroes: Readonly<PadroesDaAgenda>): PadroesDaAgenda {
  const valores = Object.fromEntries(
    PADROES_DA_AGENDA.flatMap(({ chave }) => {
      const valor = padroes[chave]?.trim() ?? '';
      return valor === '' ? [] : [[chave, valor]];
    }),
  ) as PadroesDaAgenda;
  const ocultos = PADROES_DA_AGENDA.map(({ chave }) => chave).filter(
    (chave) => padroes.ocultos?.includes(chave) && valores[chave] !== undefined,
  );
  return ocultos.length > 0 ? { ...valores, ocultos } : valores;
}

/** O campo aparece no agendamento? Escondido só quando tem padrão. */
export function campoVisivel(padroes: Readonly<PadroesDaAgenda>, chave: CampoComPadrao): boolean {
  return !(padroes.ocultos?.includes(chave) && (padroes[chave]?.trim() ?? '') !== '');
}

/** Liga ou desliga o "Campo visível" de um padrão. */
export function comVisivel(
  padroes: Readonly<PadroesDaAgenda>,
  chave: CampoComPadrao,
  visivel: boolean,
): PadroesDaAgenda {
  const outros = (padroes.ocultos ?? []).filter((c) => c !== chave);
  const ocultos = visivel ? outros : [...outros, chave];
  const { ocultos: _antes, ...resto } = padroes;
  return ocultos.length > 0 ? { ...resto, ocultos } : resto;
}

/** Os padrões mudaram em relação aos gravados? */
export function padroesMudaram(
  rascunho: Readonly<PadroesDaAgenda>,
  gravado: Readonly<PadroesDaAgenda>,
): boolean {
  const a = padroesParaGravar(rascunho);
  const b = padroesParaGravar(gravado);
  return (
    PADROES_DA_AGENDA.some(({ chave }) => a[chave] !== b[chave]) ||
    (a.ocultos ?? []).join(',') !== (b.ocultos ?? []).join(',')
  );
}
